import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { GetLocations } from "src/apis/locationApis";

const CACHE_EXPIRY = 30 * 60;

interface LocationData {
  locations: any[];
  locationId: number | null;
}

export interface LocationDataState {
  data: LocationData | null;
  lastFetched: number | null;
  loading: boolean;
  error: string | null;
}

const initialState: LocationDataState = {
  data: null,
  lastFetched: null,
  loading: false,
  error: null,
};

export const fetchLocations = createAsyncThunk<
  LocationData,
  { clientId: string | null },
  { rejectValue: string }
>(
  "locationData/fetchLocations",
  async ({ clientId }, { getState, rejectWithValue }) => {
    const state = (getState() as any).locationData;
    const now = Math.floor(Date.now() / 1000);

    if (
      state.data &&
      state.lastFetched &&
      now - state.lastFetched < CACHE_EXPIRY
    ) {
      return state.data;
    }

    if (!clientId) {
      return rejectWithValue("Client ID is required");
    }

    try {
      const locationsResp = await GetLocations(clientId);

      const filterData: LocationData = {
        locations: locationsResp.data?.records || [],
        locationId:
          locationsResp.data?.records.length === 1
            ? locationsResp.data?.records[0].locationId
            : null,
      };

      return filterData;
    } catch (error) {
      console.error("Error fetching common filters:", error);
      return rejectWithValue("Failed to fetch filter data");
    }
  }
);

const locationsSlice = createSlice({
  name: "locationData",
  initialState,
  reducers: {
    clearLocation: (state) => {
      state.data = null;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLocations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchLocations.fulfilled,
        (state, action: PayloadAction<LocationData>) => {
          state.loading = false;
          state.data = action.payload;
          state.lastFetched = Math.floor(Date.now() / 1000);
          state.error = null;
        }
      )
      .addCase(fetchLocations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Unknown error";
      });
  },
});

export const { clearLocation } = locationsSlice.actions;

export default locationsSlice.reducer;
const emptyLocations: any[] = [];
export const selectLocationData = (state: any) => state.locationData.data;
export const selectLocationLoading = (state: any) => state.locationData.loading;
export const selectLocations = (state: any) =>
  state.locationData.data?.locations || emptyLocations;
export const selectLocationId = (state: any) =>
  state.locationData.data?.locationId || null;
